// src/draft/infrastructure/ui-services/draftUiService.ts

import type { AnyDraftPlayer } from "~/src/draft/domain/models/DraftPlayer";
import type { WebContext } from "~/src/Shared/Types";
import type { DraftYearFilter } from "~/src/draft/domain/models/Filters";
import { getWebContextTryoutsData } from "~/src/draft/datasources/tryoutsData";
import { useDraftPlayers } from "~/app/composables/draftPlayers";

interface DraftUiService {
  getDraftTryoutsData: (
    year: number,
    region: string,
    webContext: WebContext,
    playerType: "goalkeeper" | "player"
  ) => Promise<AnyDraftPlayer[] | undefined>;
  generateDraftTryoutsOptions: (region: string, webContext: WebContext) => DraftYearFilter[];
}

const getDraftTryoutsData = async (
  year: number,
  region: string,
  webContext: WebContext,
  playerType: "goalkeeper" | "player"
): Promise<AnyDraftPlayer[] | undefined> => {
  //todo - remove this filter. Show NO data if year is 24 or 25 in tryouts.
  if (year === 24 || year === 25) {
    return undefined;
  }

  const { getDraftPlayers } = useDraftPlayers();

  const draftsInfo = getWebContextTryoutsData(webContext);
  const draftSelected = draftsInfo[region].find(draft => draft.year === year);

  let players: AnyDraftPlayer[] = [];
  if (draftSelected && draftSelected.data.isOld) {
    const jsonData = import(`~/src/draft/datasources/${webContext}/${region}/${year}/${playerType}.json`)
      .then(data => {
        return data.default.sort((a, b) => {
          const draftJerseyA = Number(a.draftJersey);
          const draftJerseyB = Number(b.draftJersey);

          if (Number.isNaN(draftJerseyA) || draftJerseyA <= 0) return 1;
          if (Number.isNaN(draftJerseyB) || draftJerseyB <= 0) return -1;

          return draftJerseyA - draftJerseyB;
        });
      })
      .catch(() => {
        return undefined;
      });

    players = await jsonData;
  } else if (draftSelected && !draftSelected.data.isOld && !!draftSelected.data.draftId) {
    players = (await getDraftPlayers(draftSelected.data.draftId, playerType)) || [];
  }

  if (!!players) {
    return players;
  } else {
    console.error(`Error getting draft for: /${webContext}/${region}/${year}/${playerType}`);
    return undefined;
  }
};

const generateDraftTryoutsOptions = (region: string, webContext: WebContext): DraftYearFilter[] => {
  const draftInfo = getWebContextTryoutsData(webContext);

  const draftRegionOptions = draftInfo[region].map(option => {
    return {
      id: option.id,
      year: option.year,
      name: option.name,
    };
  });

  return draftRegionOptions.sort((a, b) => b.year - a.year);
};

const draftUiService = (): DraftUiService => ({
  getDraftTryoutsData,
  generateDraftTryoutsOptions,
});

export default draftUiService;
